import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  Clock,
  Play,
  Stop,
  CircleNotch,
  CalendarBlank,
  Timer,
  User,
  ClipboardText,
  CaretDown,
  CaretUp,
  Airplane,
  Check,
  X,
  Plus,
  Trash
} from '@phosphor-icons/react';

function ClockWidget({ status, onClockIn, onClockOut, loading }) {
  const isActive = status?.clocked_in;
  const entry = status?.active_entry;

  return (
    <div className={`rounded-2xl border-2 p-6 ${isActive ? 'border-green-300 bg-green-50' : 'border-slate-200 bg-white'}`} data-testid="clock-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isActive ? 'bg-green-500' : 'bg-slate-200'}`}>
            <Clock size={24} className={isActive ? 'text-white' : 'text-slate-500'} />
          </div>
          <div>
            <p className="font-bold text-lg text-slate-900">
              {isActive ? 'Trabajando' : 'Sin registro activo'}
            </p>
            {isActive && entry && (
              <p className="text-sm text-green-700">
                Desde {new Date(entry.clock_in).toLocaleTimeString('es-ES')} &middot; {entry.elapsed_hours?.toFixed(1)}h
              </p>
            )}
          </div>
        </div>
        {isActive ? (
          <button
            onClick={onClockOut}
            disabled={loading}
            className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 font-semibold disabled:opacity-50 shadow-lg"
            data-testid="clock-out-btn"
          >
            {loading ? <CircleNotch size={20} className="animate-spin" /> : <Stop size={20} weight="fill" />}
            Clock Out
          </button>
        ) : (
          <button
            onClick={onClockIn}
            disabled={loading}
            className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 font-semibold disabled:opacity-50 shadow-lg"
            data-testid="clock-in-btn"
          >
            {loading ? <CircleNotch size={20} className="animate-spin" /> : <Play size={20} weight="fill" />}
            Clock In
          </button>
        )}
      </div>
    </div>
  );
}

function EntryRow({ entry }) {
  const clockIn = entry.clock_in ? new Date(entry.clock_in) : null;
  const clockOut = entry.clock_out ? new Date(entry.clock_out) : null;

  return (
    <div className="flex items-center gap-4 py-3 border-b border-slate-100 last:border-0" data-testid={`entry-${entry.id}`}>
      <div className={`w-2 h-8 rounded-full ${entry.status === 'active' ? 'bg-green-500' : 'bg-slate-300'}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-slate-800">{entry.date}</p>
          {entry.status === 'active' && <span className="px-1.5 py-0.5 text-[10px] bg-green-100 text-green-700 rounded font-semibold">ACTIVO</span>}
        </div>
        <p className="text-xs text-slate-500">
          {clockIn?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
          {clockOut ? ` - ${clockOut.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}` : ' - en curso'}
        </p>
        {entry.notes && <p className="text-xs text-slate-400 truncate mt-0.5">{entry.notes}</p>}
      </div>
      <div className="text-right">
        <p className="text-sm font-bold text-slate-800">{entry.hours?.toFixed(1) || '0.0'}h</p>
      </div>
    </div>
  );
}

function TeamSummary({ summary }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="team-summary">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between p-4 hover:bg-slate-50">
        <div className="flex items-center gap-2">
          <User size={18} className="text-slate-500" />
          <p className="font-semibold text-slate-800">Resumen del Equipo ({summary.length} empleados)</p>
        </div>
        {expanded ? <CaretUp size={16} /> : <CaretDown size={16} />}
      </button>
      {expanded && (
        <div className="border-t border-slate-100">
          <div className="grid grid-cols-4 gap-2 px-4 py-2 bg-slate-50 text-xs font-semibold text-slate-500">
            <span>Nombre</span><span>Rol</span><span>Horas</span><span>Dias</span>
          </div>
          {summary.map(s => (
            <div key={s.user_id} className="grid grid-cols-4 gap-2 px-4 py-2.5 border-t border-slate-50 text-sm">
              <span className="font-medium text-slate-800 truncate">{s.user_name}</span>
              <span className="text-slate-500 capitalize">{s.role}</span>
              <span className="font-bold text-slate-800">{s.total_hours}h</span>
              <span className="text-slate-500">{s.days_worked}d</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const PTO_TYPE_LABELS = {
  vacation: 'Vacaciones',
  sick: 'Enfermedad',
  personal: 'Personal',
  other: 'Otro'
};

const PTO_STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700'
};

function PTORequestForm({ onSubmit, loading }) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [ptoType, setPtoType] = useState('vacation');
  const [notes, setNotes] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!startDate || !endDate) return;
    onSubmit({ start_date: startDate, end_date: endDate, pto_type: ptoType, notes });
    setStartDate(''); setEndDate(''); setNotes('');
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-testid="pto-request-form">
      <div className="flex items-center gap-2 mb-1">
        <Airplane size={20} className="text-blue-500" />
        <h3 className="font-semibold text-slate-800">Solicitar Dias Libres</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Fecha Inicio</label>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" required data-testid="pto-start-date" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Fecha Fin</label>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" required data-testid="pto-end-date" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">Tipo</label>
        <select value={ptoType} onChange={e => setPtoType(e.target.value)}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="pto-type-select">
          <option value="vacation">Vacaciones</option>
          <option value="sick">Enfermedad</option>
          <option value="personal">Personal</option>
          <option value="other">Otro</option>
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">Notas (opcional)</label>
        <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Motivo..."
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="pto-notes" />
      </div>
      <button type="submit" disabled={loading || !startDate || !endDate}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm disabled:opacity-50"
        data-testid="pto-submit-btn">
        {loading ? <CircleNotch size={16} className="animate-spin" /> : <Plus size={16} />}
        Solicitar
      </button>
    </form>
  );
}

function PTOList({ requests, isAdmin, onApprove, onReject, onCancel, currentUserId }) {
  if (requests.length === 0) {
    return (
      <div className="py-10 text-center text-slate-400">
        <Airplane size={36} className="mx-auto mb-2 text-slate-300" />
        <p>Sin solicitudes de dias libres</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {requests.map(pto => (
        <div key={pto.id} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-100" data-testid={`pto-${pto.id}`}>
          <div className="flex-1 min-w-0">
            {isAdmin && <p className="text-xs font-semibold text-slate-600">{pto.user_name}</p>}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-slate-800">{pto.start_date} → {pto.end_date}</span>
              <span className="text-xs text-slate-500">({pto.days} dia{pto.days > 1 ? 's' : ''})</span>
              <span className={`px-1.5 py-0.5 text-[10px] rounded font-semibold ${PTO_STATUS_STYLES[pto.status] || ''}`}>
                {pto.status?.toUpperCase()}
              </span>
            </div>
            <p className="text-xs text-slate-500">{PTO_TYPE_LABELS[pto.pto_type] || pto.pto_type}{pto.notes ? ` - ${pto.notes}` : ''}</p>
            {pto.admin_notes && <p className="text-xs text-slate-400 mt-0.5">Admin: {pto.admin_notes}</p>}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {isAdmin && pto.status === 'pending' && (
              <>
                <button onClick={() => onApprove(pto.id)} className="p-1.5 rounded-lg bg-green-50 text-green-600 hover:bg-green-100" data-testid={`pto-approve-${pto.id}`}>
                  <Check size={16} weight="bold" />
                </button>
                <button onClick={() => onReject(pto.id)} className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100" data-testid={`pto-reject-${pto.id}`}>
                  <X size={16} weight="bold" />
                </button>
              </>
            )}
            {(pto.status === 'pending' && pto.user_id === currentUserId) && (
              <button onClick={() => onCancel(pto.id)} className="p-1.5 rounded-lg bg-slate-50 text-slate-500 hover:bg-slate-100" data-testid={`pto-cancel-${pto.id}`}>
                <Trash size={16} />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function HRMSPage() {
  const [activeTab, setActiveTab] = useState('clock');
  const [clockStatus, setClockStatus] = useState(null);
  const [entries, setEntries] = useState([]);
  const [totalHours, setTotalHours] = useState(0);
  const [teamSummary, setTeamSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clockLoading, setClockLoading] = useState(false);
  const [daysFilter, setDaysFilter] = useState(7);
  const [ptoRequests, setPtoRequests] = useState([]);
  const [allPtoRequests, setAllPtoRequests] = useState([]);
  const [ptoLoading, setPtoLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUserId, setCurrentUserId] = useState('');
  const [ptoBalance, setPtoBalance] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [statusRes, entriesRes] = await Promise.all([
        axios.get(`${API_URL}/api/hrms/status`, { withCredentials: true }),
        axios.get(`${API_URL}/api/hrms/my-entries?days=${daysFilter}`, { withCredentials: true })
      ]);
      setClockStatus(statusRes.data);
      setEntries(entriesRes.data.entries || []);
      setTotalHours(entriesRes.data.total_hours || 0);

      try {
        const teamRes = await axios.get(`${API_URL}/api/hrms/team-summary?days=${daysFilter}`, { withCredentials: true });
        setTeamSummary(teamRes.data.summary || []);
        setIsAdmin(true);
      } catch { setTeamSummary([]); }

      // Fetch user info for PTO
      try {
        const meRes = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
        setCurrentUserId(meRes.data.id);
        setIsAdmin(['admin', 'manager'].includes(meRes.data.role));
      } catch {}
    } catch (err) {
      console.error('HRMS error:', err);
    } finally {
      setLoading(false);
    }
  }, [daysFilter]);

  const fetchPTO = useCallback(async () => {
    try {
      const [myRes, balanceRes] = await Promise.all([
        axios.get(`${API_URL}/api/hrms/pto/my-requests`, { withCredentials: true }),
        axios.get(`${API_URL}/api/hrms/pto/balance`, { withCredentials: true })
      ]);
      setPtoRequests(myRes.data || []);
      setPtoBalance(balanceRes.data || null);
      try {
        const allRes = await axios.get(`${API_URL}/api/hrms/pto/all`, { withCredentials: true });
        setAllPtoRequests(allRes.data || []);
      } catch { setAllPtoRequests([]); }
    } catch (err) {
      console.error('PTO error:', err);
    }
  }, []);

  useEffect(() => { fetchData(); fetchPTO(); }, [fetchData, fetchPTO]);

  const handleClockIn = async () => {
    setClockLoading(true);
    try {
      let lat = null, lng = null;
      try {
        const pos = await new Promise((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 })
        );
        lat = pos.coords.latitude; lng = pos.coords.longitude;
      } catch {}
      await axios.post(`${API_URL}/api/hrms/clock-in`, { latitude: lat, longitude: lng }, { withCredentials: true });
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al hacer clock-in');
    } finally { setClockLoading(false); }
  };

  const handleClockOut = async () => {
    setClockLoading(true);
    try {
      let lat = null, lng = null;
      try {
        const pos = await new Promise((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 })
        );
        lat = pos.coords.latitude; lng = pos.coords.longitude;
      } catch {}
      await axios.post(`${API_URL}/api/hrms/clock-out`, { latitude: lat, longitude: lng }, { withCredentials: true });
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al hacer clock-out');
    } finally { setClockLoading(false); }
  };

  const handleRequestPTO = async (data) => {
    setPtoLoading(true);
    try {
      await axios.post(`${API_URL}/api/hrms/pto/request`, data, { withCredentials: true });
      await fetchPTO();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al solicitar PTO');
    } finally { setPtoLoading(false); }
  };

  const handleApprovePTO = async (id) => {
    try {
      await axios.put(`${API_URL}/api/hrms/pto/${id}`, { status: 'approved' }, { withCredentials: true });
      await fetchPTO();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleRejectPTO = async (id) => {
    try {
      await axios.put(`${API_URL}/api/hrms/pto/${id}`, { status: 'rejected' }, { withCredentials: true });
      await fetchPTO();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleCancelPTO = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/hrms/pto/${id}`, { withCredentials: true });
      await fetchPTO();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <CircleNotch size={32} className="animate-spin text-blue-600" />
      </div>
    );
  }

  const pendingPtoCount = (isAdmin ? allPtoRequests : ptoRequests).filter(p => p.status === 'pending').length;

  return (
    <div className="space-y-6" data-testid="hrms-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Control de Horas</h1>
          <p className="text-slate-500">Clock in/out, registro de tiempo y dias libres</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Tabs */}
          <div className="flex bg-slate-100 rounded-lg p-1" data-testid="hrms-tabs">
            <button onClick={() => setActiveTab('clock')}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition ${activeTab === 'clock' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
              data-testid="tab-clock">
              <Clock size={14} className="inline mr-1" /> Horas
            </button>
            <button onClick={() => setActiveTab('pto')}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition relative ${activeTab === 'pto' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
              data-testid="tab-pto">
              <Airplane size={14} className="inline mr-1" /> Dias Libres
              {pendingPtoCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">{pendingPtoCount}</span>
              )}
            </button>
          </div>
          {activeTab === 'clock' && (
            <select value={daysFilter} onChange={e => setDaysFilter(Number(e.target.value))}
              className="px-4 py-2.5 border border-slate-200 rounded-lg bg-white text-sm" data-testid="hrms-days-filter">
              <option value={1}>Hoy</option>
              <option value={7}>Ultima semana</option>
              <option value={14}>Ultimas 2 semanas</option>
              <option value={30}>Ultimo mes</option>
            </select>
          )}
        </div>
      </div>

      {activeTab === 'clock' && (
        <>
          <ClockWidget status={clockStatus} onClockIn={handleClockIn} onClockOut={handleClockOut} loading={clockLoading} />

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <Timer size={20} className="text-blue-500 mb-2" />
              <p className="text-2xl font-bold text-slate-900">{totalHours}h</p>
              <p className="text-xs text-slate-500">Horas totales</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <CalendarBlank size={20} className="text-green-500 mb-2" />
              <p className="text-2xl font-bold text-slate-900">{entries.length}</p>
              <p className="text-xs text-slate-500">Registros</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <ClipboardText size={20} className="text-amber-500 mb-2" />
              <p className="text-2xl font-bold text-slate-900">{entries.length > 0 ? (totalHours / Math.max(new Set(entries.map(e => e.date)).size, 1)).toFixed(1) : '0.0'}h</p>
              <p className="text-xs text-slate-500">Promedio/dia</p>
            </div>
          </div>

          {teamSummary.length > 0 && <TeamSummary summary={teamSummary} />}

          <div className="bg-white rounded-xl border border-slate-200" data-testid="entries-list">
            <div className="px-4 py-3 border-b border-slate-100">
              <p className="font-semibold text-slate-800">Mis Registros</p>
            </div>
            {entries.length > 0 ? (
              <div className="px-4 py-2">
                {entries.map(entry => <EntryRow key={entry.id} entry={entry} />)}
              </div>
            ) : (
              <div className="py-12 text-center text-slate-400">
                <Clock size={40} className="mx-auto mb-2 text-slate-300" />
                <p>Sin registros en este periodo</p>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'pto' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <PTORequestForm onSubmit={handleRequestPTO} loading={ptoLoading} />

            {/* PTO Balance Card */}
            {ptoBalance && (
              <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-testid="pto-balance-card">
                <div className="flex items-center gap-2 mb-1">
                  <CalendarBlank size={20} className="text-emerald-500" />
                  <h3 className="font-semibold text-slate-800">Balance Anual {ptoBalance.year}</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-blue-700">{ptoBalance.annual_days}</p>
                    <p className="text-[10px] uppercase font-semibold text-blue-500">Total Dias</p>
                  </div>
                  <div className={`rounded-lg p-3 text-center ${ptoBalance.remaining_days > 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                    <p className={`text-2xl font-bold ${ptoBalance.remaining_days > 0 ? 'text-emerald-700' : 'text-red-700'}`}>{ptoBalance.remaining_days}</p>
                    <p className={`text-[10px] uppercase font-semibold ${ptoBalance.remaining_days > 0 ? 'text-emerald-500' : 'text-red-500'}`}>Disponibles</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-slate-700">{ptoBalance.used_days}</p>
                    <p className="text-[10px] uppercase font-semibold text-slate-500">Usados</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-amber-700">{ptoBalance.pending_days}</p>
                    <p className="text-[10px] uppercase font-semibold text-amber-500">Pendientes</p>
                  </div>
                </div>
                {ptoBalance.over_limit && (
                  <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-lg">
                    Has excedido tu limite anual de dias libres
                  </div>
                )}
                {/* Progress bar */}
                <div className="space-y-1">
                  <div className="flex justify-between text-[10px] text-slate-500">
                    <span>{ptoBalance.used_days} usados de {ptoBalance.annual_days}</span>
                    <span>{Math.round((ptoBalance.used_days / Math.max(ptoBalance.annual_days, 1)) * 100)}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${ptoBalance.over_limit ? 'bg-red-500' : 'bg-blue-500'}`}
                      style={{ width: `${Math.min((ptoBalance.used_days / Math.max(ptoBalance.annual_days, 1)) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-slate-200" data-testid="pto-my-list">
            <div className="px-4 py-3 border-b border-slate-100">
              <p className="font-semibold text-slate-800">Mis Solicitudes</p>
            </div>
            <div className="p-3">
              <PTOList requests={ptoRequests} isAdmin={false} onCancel={handleCancelPTO} currentUserId={currentUserId} />
            </div>
          </div>

          {isAdmin && allPtoRequests.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200" data-testid="pto-admin-list">
              <div className="px-4 py-3 border-b border-slate-100">
                <p className="font-semibold text-slate-800">Todas las Solicitudes (Admin)</p>
              </div>
              <div className="p-3">
                <PTOList requests={allPtoRequests} isAdmin={true}
                  onApprove={handleApprovePTO} onReject={handleRejectPTO} onCancel={handleCancelPTO}
                  currentUserId={currentUserId} />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
